<template>
  <VaSidebar v-model="writableVisible" :width="sidebarWidth" :color="color" minimized-width="0">
    <!-- single select shown once -->
    <div class="px-4 py-3">
      <VaSelect
        v-model="selectedVenue"
        class="w-[10rem] sm:w-[unset]"
        :options="selectableVenues"
        item-value="value"
        item-text="label"
      />
    </div>
    <VaAccordion v-model="value" multiple>
      <VaCollapse v-for="(route, index) in navigationRoutes.routes" :key="index">
        <template #header="{ value: isCollapsed }">
          <VaSidebarItem
            :to="route.children ? undefined : { name: route.name }"
            :active="routeHasActiveChild(route)"
            :active-color="activeColor"
            :text-color="textColor(route)"
            :aria-label="`${route.children ? 'Open category ' : 'Visit'} ${t(route.displayName)}`"
            role="button"
            hover-opacity="0.10"
          >
            <VaSidebarItemContent class="py-3 pr-2 pl-4">
              <VaIcon
                v-if="route.meta.icon"
                aria-hidden="true"
                :name="route.meta.icon"
                size="20px"
                :color="iconColor(route)"
              />
              <VaSidebarItemTitle class="flex justify-between items-center leading-5 font-semibold">
                {{ t(route.displayName) }}
                <VaIcon v-if="route.children" :name="arrowDirection(isCollapsed)" size="20px" />
              </VaSidebarItemTitle>
            </VaSidebarItemContent>
          </VaSidebarItem>
        </template>
        <template #body>
          <div v-for="(childRoute, index2) in route.children" :key="index2">
            <VaSidebarItem
              :to="{ name: childRoute.name }"
              :active="isActiveChildRoute(childRoute)"
              :active-color="activeColor"
              :text-color="textColor(childRoute)"
              :aria-label="`Visit ${t(route.displayName)}`"
              hover-opacity="0.10"
            >
              <VaSidebarItemContent class="py-3 pr-2 pl-11">
                <VaSidebarItemTitle class="leading-5 font-semibold">
                  {{ t(childRoute.displayName) }}
                </VaSidebarItemTitle>
              </VaSidebarItemContent>
            </VaSidebarItem>
          </div>
        </template>
      </VaCollapse>
    </VaAccordion>
  </VaSidebar>
</template>
<script lang="ts">
import { defineComponent, watch, ref, computed, inject } from 'vue'
import { useRoute } from 'vue-router'

import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'

import { createNavigationRoutes, type INavigationRoute } from './NavigationRoutes'
import { ApiClient } from '@/data/api'
import { useVenueStore } from '@/stores/venue'

export default defineComponent({
  name: 'Sidebar',
  props: {
    visible: { type: Boolean, default: true },
    mobile: { type: Boolean, default: false },
  },
  emits: ['update:visible'],

  setup: (props, { emit }) => {
    const { getColor, colorToRgba } = useColors()
    const route = useRoute()
    const { t } = useI18n()
    const store = useVenueStore()

    const value = ref<boolean[]>([])

    const writableVisible = computed({
      get: () => props.visible,
      set: (v: boolean) => emit('update:visible', v),
    })

    const isActiveChildRoute = (child: INavigationRoute) => route.name === child.name

    const routeHasActiveChild = (section: INavigationRoute) => {
      if (!section.children) {
        return route.path.endsWith(`${section.name}`)
      }

      return section.children.some(({ name }) => route.path.endsWith(`${name}`))
    }

    // inject user (may be a ref or plain object)
    const injectedUser = inject('user') as any
    const userVal = computed(() => (injectedUser && typeof injectedUser === 'object' && 'value' in injectedUser) ? injectedUser.value : injectedUser)
    const apiClient = new ApiClient()

    if (userVal.value.role_id === 1 || userVal.value.is_superuser) {
      apiClient.getAllVenues().then((response) => {
        if (response.ok) {
          console.log('AppSidebar: loaded all venues for admin user')
          console.log('User is admin, all venues:', response.result)
          userVal.value.venues = [{id: 'all', name: 'All Venues'}, ...response.result]
        } else {
          console.error('Failed to load all venues for admin user')
        }
      })
    } else {
      console.log('User is not admin, venues:', userVal.value?.venues)
    }

    const selectedVenue = ref<any>(null)

    // pure computed mapping from user venues -> options (no side-effects)
    const selectableVenues = computed(() => {
      const venues = Array.isArray(userVal.value?.venues) ? userVal.value.venues : []
      return venues.map((v: any) => ({
        label: v.name ?? String(v.id),
        text: v.name ?? String(v.id),
        value: String(v.id),
      }))
    })

    watch(
      selectableVenues,
      (opts) => {
        if (opts && opts.length && selectedVenue.value == null) {
          for (const option of opts) {
            if (option.value === String(store.venue_id)) {
              selectedVenue.value = option
              console.log('AppSidebar: initializing selectedVenue to store venue_id', store.venue_id)
              return
            }
          }
          selectedVenue.value = opts[0] ?? null
        }
      },
      { immediate: true }
    )

    watch(selectedVenue, (newVenue) => {
      if (newVenue && newVenue.value) {
        console.log('AppSidebar: selectedVenue changed to', newVenue)
        store.setVenue(newVenue.value)
      }
    })

    // compute navigation routes from current user
    const navigationRoutes = computed(() => createNavigationRoutes(userVal.value))

    const setActiveExpand = () =>
      (value.value = (navigationRoutes.value.routes || []).map((route: INavigationRoute) => routeHasActiveChild(route)))

    const sidebarWidth = computed(() => (props.mobile ? '100vw' : '280px'))
    const color = computed(() => getColor('background-secondary'))
    const activeColor = computed(() => colorToRgba(getColor('focus'), 0.1))

    const iconColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'secondary')
    const textColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'textPrimary')
    const arrowDirection = (state: boolean) => (state ? 'va-arrow-up' : 'va-arrow-down')

    watch(() => route.fullPath, setActiveExpand, { immediate: true })

    return {
      writableVisible,
      sidebarWidth,
      value,
      color,
      activeColor,
      navigationRoutes,
      routeHasActiveChild,
      isActiveChildRoute,
      t,
      iconColor,
      textColor,
      arrowDirection,
      selectedVenue,
      selectableVenues,
    }
  },
})
</script>
